<template>
  <InputFrame
    :title="
      psc?.type === PSCType.Kaizen
        ? t(`views.psc.CreateKaizen.titleUpdate`)
        : t(`views.psc.Create.createPSCHeadlineUpdate`)
    "
    :loading="loading"
    :delete-able="canDelete"
    :display-mode="showInModal ? InputFrameMode.modal : undefined"
    :submit="
      async () => {
        await updatePSC();
        return { success: true };
      }
    "
    @delete="deletePSC"
    @cancel="emit('update:visible-action-modal', false)"
  >
    <template v-if="psc != null">
      <InputRow
        :model-value="psc.teamLeadId"
        :display-name="t('views.psc.Create.teamLeader')"
        class="doNotPrintMe"
        name="teamLeadId"
        required
      >
        <template #default="{ field: inputField }">
          <SearchSelect
            v-bind="inputField"
            v-model="psc.teamLeadId"
            name="teamLead"
            :mode="SelectMode.USER"
            data-testid="teamLead"
            :placeholder="t('views.psc.Create.teamLeaderPlaceholder')"
            @update:model-value="removeFromMembers"
          />
        </template>
      </InputRow>
      <InputRow
        :model-value="psc.teamMembers"
        :display-name="t('views.psc.Create.teamMembers')"
        class="doNotPrintMe"
        name="teamMembers"
      >
        <template #default="{ field: inputField }">
          <SearchSelect
            v-bind="inputField"
            v-model="psc.teamMembers"
            :mode="SelectMode.USER"
            name="teamMembers"
            :multiple="true"
            :placeholder="t('views.psc.Create.memberPlaceholder')"
            :exclude="psc.teamLeadId ? [psc.teamLeadId] : []"
          />
        </template>
      </InputRow>
      <PSCComponent v-model:psc="psc" />
    </template>

    <template #leading>
      <div class="flex justify-content-betwee gap-2 mr-2">
        <LISAButton class="p-button-info" :disabled="showInModal" @click="printMe">
          <i class="fas fa-print pr-1" />{{ t("views.psc.Edit.print") }}
          <InfoTooltip
            v-if="showInModal"
            :text="t('views.psc.Edit.printDenied', { EABName: eabName })"
          />
        </LISAButton>
      </div>
    </template>
  </InputFrame>
</template>

<script setup lang="ts">
import InputFrame from "@/components/shared/form/InputFrame.vue";
import InputRow from "@/components/shared/form/InputRow.vue";
import {
  ActionType,
  PSCType,
  usePSCDeleteMutation,
  usePSCGetQuery,
  usePSCUpdateMutation,
} from "@/graphql";
import { computed } from "vue";
import PSCComponent from "../components/PSCComponent.vue";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";
import { useRouter } from "vue-router";
import LISAButton from "@/components/shared/primevue/LISAButton.vue";
import useMutateGraphQL from "@/functions/useMutateGraphQL";
import type { PSCEditTypeUnknow } from "../helper/PSCTypes";
import notEmpty from "@/functions/notEmpty";
import { InputFrameMode } from "@/components/shared/form/InputFrameMode";
import InfoTooltip from "@/components/shared/InfoTooltip.vue";
import { allowNextNavigation } from "@/functions/useUnsavedChanges";
import { useTranslationFunctionCode } from "@/components/menu/SidebarHelper";
import { useTranslation } from "@/language/translation";
import store from "@/store";

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  showInModal: {
    type: Boolean,
    required: false,
    default: false,
  },
});
const { t } = useTranslation();
const canDelete = computed(() => store.getters["login/hasAccessRight"]("psc.admin"));

function removeFromMembers(leaderId?: number) {
  if (leaderId == null || psc.value == null) {
    return;
  }
  const toRemove = psc.value.teamMembers.findIndex((member) => member === leaderId);
  if (toRemove != null && toRemove !== -1) {
    psc.value.teamMembers.splice(toRemove, 1);
  }
}

const translationFunctionCode = useTranslationFunctionCode();
const eabName = computed(() => t(translationFunctionCode("EABName")!));

const router = useRouter();

const { mutableResult: psc, loading: loadingGet } = useMutateGraphQL(
  usePSCGetQuery,
  () => ({
    ids: props.id,
  }),
  undefined,
  (res): undefined | PSCEditTypeUnknow => {
    if (res?.PSCs?.[0] == null) {
      return undefined;
    }
    return {
      ...res.PSCs[0],
      Area1: {
        ...res.PSCs[0].Area1,
        issue: res.PSCs[0].Area1?.issue ?? false,
        fileIds: res.PSCs[0].Area1?.Folder?.files.map((file) => file.id) ?? [],
      },
      Area1K: {
        ...res.PSCs[0].Area1K,
        fileIds: res.PSCs[0].Area1K?.Folder?.files.map((file) => file.id) ?? [],
      },
      Area3K: {
        ...res.PSCs[0].Area3K,
        fileIds: res.PSCs[0].Area3K?.Folder?.files.map((file) => file.id) ?? [],
      },
      Area3:
        res.PSCs[0].Area3 == null
          ? undefined
          : {
              id: res.PSCs[0].Area3.id,
              tasks: res.PSCs[0].Area3.TaskConnection.map((task) => task.Task).filter(notEmpty),
            },
      Area5:
        res.PSCs[0].Area5 == null
          ? undefined
          : {
              id: res.PSCs[0].Area5.id,
              tasks: res.PSCs[0].Area5.TaskConnection.map((task) => task.Task).filter(notEmpty),
            },
      Area7:
        res.PSCs[0].Area7 == null
          ? undefined
          : {
              id: res.PSCs[0].Area7.id,
              tasks: res.PSCs[0].Area7.TaskConnection.map((task) => task.Task).filter(notEmpty),
            },
      Area8:
        res.PSCs[0].Area8 == null
          ? undefined
          : {
              id: res.PSCs[0].Area8.id,
              tasks: res.PSCs[0].Area8.TaskConnection.map((task) => task.Task).filter(notEmpty),
            },
      Area4:
        res.PSCs[0].Area4 == null
          ? undefined
          : {
              id: res.PSCs[0].Area4.id,

              fileIds: res.PSCs[0].Area4.Folder?.files.map((file) => file.id) ?? [],
              reasons: res.PSCs[0].Area4.Reasons,
            },
      Area4K:
        res.PSCs[0].Area4K == null
          ? undefined
          : {
              id: res.PSCs[0].Area4K.id,
              postIts: res.PSCs[0].Area4K.PostIts,
            },
      teamMembers: res.PSCs[0].TeamMember.map((member) => member.UserId) || [],
    };
  },
  () => true,
);

const emit = defineEmits<{
  "update:visible-action-modal": [visible: boolean];
}>();

const {
  mutate: updatePSCMutation,
  loading: laodingUpdate,
  onDone: onDoneUpdate,
} = usePSCUpdateMutation();
const {
  mutate: deletePSCMutation,
  loading: laodingDelete,
  onDone: onDoneDelete,
} = usePSCDeleteMutation();

const loading = computed(() => loadingGet.value || laodingUpdate.value || laodingDelete.value);

async function updatePSC() {
  if (psc.value == null) {
    return;
  }
  await updatePSCMutation(
    {
      id: props.id,
      psc: {
        closed: psc.value.closed,
        type: psc.value.type,
        problem: psc.value.problem ?? "",
        hashtags: psc.value.hashtags,
        siteId: psc.value.siteId,
        teamLeadId: psc.value.teamLeadId!,
        teamMembers: psc.value.teamMembers,
        Area1:
          psc.value.Area1 == null
            ? undefined
            : {
                detectedOn: psc.value.Area1.detectedOn,
                issue: psc.value.Area1.issue,
                discoveredFrom: psc.value.Area1.discoveredFrom ?? "",
                fileIds: psc.value.Area1.fileIds,
                area: psc.value.Area1.area,
                defects: psc.value.Area1.defects,
                goal: psc.value.Area1.goal,
                plant: psc.value.Area1.plant,
                product: psc.value.Area1.product,
              },
        Area1Kaizen:
          psc.value.Area1K == null
            ? undefined
            : {
                additionalInformation: psc.value.Area1K.additionalInformation,
                area: psc.value.Area1K.area,
                fileIds: psc.value.Area1K.fileIds,
                goalQualitative: psc.value.Area1K.goalQualitative,
                goalQuantitative: psc.value.Area1K.goalQuantitative,
                product: psc.value.Area1K.product,
              },
        Area2:
          psc.value.Area2 == null
            ? undefined
            : {
                howNOK: psc.value.Area2.howNOK,
                whereNOK: psc.value.Area2.whereNOK,
                whyNOK: psc.value.Area2.whyNOK,
                howOK: psc.value.Area2.howOK,
                whereOK: psc.value.Area2.whereOK,
                whyOK: psc.value.Area2.whyOK,
                whatNOK: psc.value.Area2.whatNOK,
                whatOK: psc.value.Area2.whatOK,
                whenNOK: psc.value.Area2.whenNOK,
                whenOK: psc.value.Area2.whenOK,
                whoNOK: psc.value.Area2.whoNOK,
                whoOK: psc.value.Area2.whoOK,
              },
        Area3:
          psc.value.Area3 == null
            ? undefined
            : {
                tasks: psc.value.Area3.tasks.map((task) => ({
                  id: task.id,
                  assigneeId: task.assigneeId!,
                  deadline: task.deadline!,
                  description: task.description,
                  status: task.status!,
                })),
              },
        Area3Kaizen:
          psc.value.Area3K == null
            ? undefined
            : {
                fileIds: psc.value.Area3K.fileIds,
              },
        Area4:
          psc.value.Area4 == null
            ? undefined
            : {
                fileIds: psc.value.Area4.fileIds,
                reasons: psc.value.Area4.reasons.map((reason) => ({
                  id: reason.id,
                  selected: reason.selected ?? 0,
                  name: reason.name,
                  why0: reason.why0,
                  why1: reason.why1,
                  why2: reason.why2,
                  why3: reason.why3,
                  why4: reason.why4,
                })),
              },
        Area4Kaizen:
          psc.value.Area4K == null
            ? undefined
            : {
                postIts: psc.value.Area4K.postIts,
              },
        Area5:
          psc.value.Area5 == null
            ? undefined
            : {
                tasks: psc.value.Area5.tasks.map((task) => ({
                  id: task.id,
                  assigneeId: task.assigneeId!,
                  deadline: task.deadline!,
                  description: task.description,
                  status: task.status!,
                })),
              },
        Area6:
          psc.value.Area6 == null
            ? undefined
            : {
                evidence: psc.value.Area6.evidence,
              },
        Area6Kaizen:
          psc.value.Area6K == null
            ? undefined
            : {
                datapoints: psc.value.Area6K.datapoints,
                goal: psc.value.Area6K.goal,
                intervall: psc.value.Area6K.intervall,
              },
        Area7:
          psc.value.Area7 == null
            ? undefined
            : {
                tasks: psc.value.Area7.tasks.map((task) => ({
                  id: task.id,
                  assigneeId: task.assigneeId!,
                  deadline: task.deadline!,
                  description: task.description,
                  status: task.status!,
                })),
              },
        Area8:
          psc.value.Area8 == null
            ? undefined
            : {
                tasks: psc.value.Area8.tasks.map((task) => ({
                  id: task.id,
                  assigneeId: task.assigneeId!,
                  deadline: task.deadline!,
                  description: task.description,
                  status: task.status!,
                })),
              },
        Area9:
          psc.value.Area9 == null
            ? undefined
            : {
                controlCircuit: psc.value.Area9.controlCircuit,
                finished: psc.value.Area9.finished,
              },
      },
    },
    {
      update(cache, { data }) {
        const updatedPSC = data?.PSCUpdate;
        if (updatedPSC == null) {
          return;
        }
        const storedObject = cache.identify({
          __typename: ActionType.PSC,
          id: updatedPSC.id,
        });
        cache.modify({
          id: storedObject,
          fields: {
            assignee() {
              return updatedPSC.TeamLead!;
            },
            status() {
              return updatedPSC.closed ? 4 : 1;
            },
            deadline() {
              return updatedPSC.createdAt;
            },
          },
          broadcast: true,
          optimistic: true,
        });
      },
    },
  );

  if (!props.showInModal) {
    router.push({
      name: "PSCOverview",
      params: { type: psc.value.type, archive: psc.value.closed.toString() },
    });
  } else {
    emit("update:visible-action-modal", false);
  }
}

async function deletePSC() {
  await deletePSCMutation({
    id: props.id,
  });

  if (!props.showInModal) {
    router.push({
      name: "PSC",
    });
  } else {
    emit("update:visible-action-modal", false);
  }
}

onDoneUpdate(() => {
  if (psc.value == null) {
    return;
  }
  if (!props.showInModal) {
    allowNextNavigation();
    router.push({
      name: "PSCOverview",
      params: { type: psc.value.type, archive: psc.value.closed.toString() },
    });
  } else {
    emit("update:visible-action-modal", false);
  }
});

onDoneDelete(() => {
  if (!props.showInModal) {
    allowNextNavigation();
    router.push({
      name: "PSC",
    });
  } else {
    emit("update:visible-action-modal", false);
  }
});

function printMe() {
  window.print();
}
</script>

<style lang="scss" scoped></style>
